import QRCode from "qrcode";
import { useCallback, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useAsyncData } from "../../hooks/useAsyncData";
import { routeToHash } from "../../routes";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { formatTimestamp } from "../../utils/format";
import { Button } from "../shared/Button";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  panel: {
    marginTop: 10,
    padding: 14,
    borderRadius: 6,
    background: theme.color.background,
    display: "grid",
    gap: 10,
  },
  share: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  qr: {
    width: 148,
    height: 148,
    background: "#fff",
    borderRadius: 6,
    border: `1px solid ${theme.color.border}`,
  },
  linkBox: { flex: "1 1 260px", minWidth: 0, display: "grid", gap: 8 },
  link: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 12,
    padding: "8px 10px",
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: 6,
    wordBreak: "break-all",
  },
  signers: { margin: 0, paddingLeft: 18, lineHeight: 1.6 },
  muted: { color: theme.color.muted, margin: 0 },
}));

interface GuestWaiverPanelProps {
  reservationId: number;
  partySize: number;
}

/** The trip's guest waiver link and QR code, and who has signed so far. */
export const GuestWaiverPanel = ({
  reservationId,
  partySize,
}: GuestWaiverPanelProps) => {
  const classes = useStyles();
  const [qr, setQr] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const load = useCallback(
    (signal: AbortSignal) =>
      trpc.reservation.guestWaivers.query({ id: reservationId }, { signal }),
    [reservationId]
  );
  const waivers = useAsyncData(load);
  const token = waivers.data?.token;
  const link = token
    ? `${window.location.origin}/${routeToHash({ page: "guestWaiver", token })}`
    : undefined;

  useEffect(() => {
    if (!link) {
      return undefined;
    }
    let cancelled = false;
    QRCode.toDataURL(link, { margin: 1, width: 296 }).then(
      (url) => {
        if (!cancelled) {
          setQr(url);
        }
      },
      () => undefined
    );
    return () => {
      cancelled = true;
    };
  }, [link]);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (waivers.error) {
    return <LoadError message={waivers.error} onRetry={waivers.reload} />;
  }
  if (!waivers.data || !link) {
    return <Loading label="Loading guest waivers…" />;
  }
  const signed = waivers.data.signatures;
  const guestsExpected = Math.max(0, partySize - 1);

  return (
    <div className={classes.panel}>
      <Notice kind="info">
        {
          "You are responsible for making sure every adult aboard signs this waiver before departure, and that a parent or guardian signs for every child. If anyone boards without signing, any claim by or through them falls on you."
        }
      </Notice>
      <div className={classes.share}>
        {qr && (
          <img
            className={classes.qr}
            src={qr}
            alt="QR code for the guest waiver link"
          />
        )}
        <div className={classes.linkBox}>
          <strong>Share this link with everyone coming aboard</strong>
          <div className={classes.link}>{link}</div>
          <div>
            <Button
              variant="secondary"
              small
              onClick={() => {
                void navigator.clipboard
                  .writeText(link)
                  .then(() => setCopied(true));
              }}
            >
              {copied ? "Copied!" : "Copy link"}
            </Button>
          </div>
          <p className={classes.muted}>
            Guests can also scan the QR code at the dock.
          </p>
        </div>
      </div>
      <div>
        <strong>
          {`Signed so far: ${signed.length}`}
          {guestsExpected > 0
            ? ` (your party has ${guestsExpected} other ${guestsExpected === 1 ? "person" : "people"})`
            : ""}
        </strong>
        {signed.length > 0 ? (
          <ul className={classes.signers}>
            {signed.map((s) => (
              <li key={`${s.signedName}-${s.signedAt.getTime()}`}>
                {s.signedName}
                {s.minorNames
                  ? ` (also signed for ${s.minorNames})`
                  : ""} · {formatTimestamp(s.signedAt)}
              </li>
            ))}
          </ul>
        ) : (
          <p className={classes.muted}>No guests have signed yet.</p>
        )}
        <Button variant="ghost" small onClick={waivers.reload}>
          Refresh
        </Button>
      </div>
    </div>
  );
};
